import {
    SimpleThreeColumns,
    SimpleTwoColumns,
    WithPanel,
    AsymmetricGrid,
    AsymmetricMosaicGrid,
} from "@/views/blocks/gallery/gallery";
import type {
    SimpleThreeColumnsProps,
    SimpleTwoColumnsProps,
    WithPanelProps,
    AsymmetricGridProps,
} from "@/views/blocks/gallery/gallery";
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import type { BlockDef } from "./block.interfaces";
import { arrayField, createBlock, headingField, htmlField } from "./blockBuilder";
import { useDefaultColors } from "./useDefaultColors";
import { useFieldConfig } from "./useFieldConfig";
import { getStockPhotoUrl } from "@/apps/page/services/page.service";
import type { AsymmetricMosaicGridProps } from "@/views/blocks/gallery/asymmetric-mosaic/AsymmetricMosaicGridProps";
import { MediaType } from "@/views/blocks/PageBlock.interface";

// Import the default video
import defaultVideoUrl from "@/views/blocks/shared/videos/default-video.mp4";

export function useGalleries() {
    const { t } = useI18n();
    const { colorDefaults } = useDefaultColors();
    const { translations } = useFieldConfig();

    const simpleThreeColumnsBlock = computed<BlockDef>(() =>
        createBlock<SimpleThreeColumnsProps>({
            typeId: "SimpleThreeColumns",
            type: "gallery",
            name: t("page.blocks.gallery.simpleThreeColumns"),
            component: SimpleThreeColumns,
        })
            .withField(
                "tag",
                headingField(translations.value.tag, "Innovation", "h3", {
                    text: translations.value.text,
                    tag: translations.value.tag,
                })
            )
            .withField(
                "title",
                headingField(
                    translations.value.title,
                    "Powering Remote Work & Collaboration",
                    "h2",
                    {
                        text: translations.value.text,
                        tag: translations.value.tag,
                    }
                )
            )
            .withField(
                "description",
                htmlField(
                    translations.value.description,
                    "See how modern professionals connect, collaborate, and innovate from anywhere in the world - bringing teams together through technology and shared purpose.",
                    translations.value.description
                )
            )
            .withArrayField(
                "images",
                arrayField(
                    translations.value.image,
                    "images",
                    {
                        mediaType: { label: translations.value.mediaType, type: "media" },
                        src: { label: translations.value.src, type: "media" },
                        alt: { label: translations.value.alt, type: "media" },
                        videoSrc: { label: translations.value.videoSrc, type: "media" },
                        videoAutoplay: { label: translations.value.videoAutoplay, type: "media" },
                        videoControls: { label: translations.value.videoControls, type: "media" },
                        videoLoop: { label: translations.value.videoLoop, type: "media" },
                        iframeSrc: { label: translations.value.iframeSrc, type: "media" },
                    },
                    [
                        {
                            mediaType: MediaType.IMAGE,
                            src: getStockPhotoUrl(
                                "communities",
                                "businessman-video-conference-call-while-working-from-home-coronavirus-pandemic.webp"
                            ),
                            alt: "Businessman video conference call while working from home",
                            videoSrc: defaultVideoUrl,
                            videoAutoplay: false,
                            videoControls: true,
                            videoLoop: false,
                            iframeSrc:
                                "https://www.youtube.com/embed/LxIR7eFw8HI?si=ObbVhSBAi35VMABl",
                        },
                        {
                            mediaType: MediaType.IMAGE,
                            src: getStockPhotoUrl(
                                "communities",
                                "cheerful-diverse-people-together-park.webp"
                            ),
                            alt: "Cheerful diverse people together in park",
                            videoSrc: defaultVideoUrl,
                            videoAutoplay: false,
                            videoControls: true,
                            videoLoop: false,
                            iframeSrc:
                                "https://www.youtube.com/embed/LxIR7eFw8HI?si=ObbVhSBAi35VMABl",
                        },
                        {
                            mediaType: MediaType.IMAGE,
                            src: getStockPhotoUrl(
                                "communities",
                                "businesswoman-having-video-conference-with-colleagues-coronavirus-quarantine.webp"
                            ),
                            alt: "Businesswoman having video conference with colleagues",
                            videoSrc: defaultVideoUrl,
                            videoAutoplay: false,
                            videoControls: true,
                            videoLoop: false,
                            iframeSrc:
                                "https://www.youtube.com/embed/LxIR7eFw8HI?si=ObbVhSBAi35VMABl",
                        },
                        {
                            mediaType: MediaType.IMAGE,
                            src: getStockPhotoUrl(
                                "communities",
                                "volunteers-posting-social-media.webp"
                            ),
                            alt: "Volunteers posting on social media",
                            videoSrc: defaultVideoUrl,
                            videoAutoplay: false,
                            videoControls: true,
                            videoLoop: false,
                            iframeSrc:
                                "https://www.youtube.com/embed/LxIR7eFw8HI?si=ObbVhSBAi35VMABl",
                        },
                        {
                            mediaType: MediaType.IMAGE,
                            src: getStockPhotoUrl(
                                "communities",
                                "medium-shot-man-working-late-night-laptop.webp"
                            ),
                            alt: "Man working late night with laptop",
                            videoSrc: defaultVideoUrl,
                            videoAutoplay: false,
                            videoControls: true,
                            videoLoop: false,
                            iframeSrc:
                                "https://www.youtube.com/embed/LxIR7eFw8HI?si=ObbVhSBAi35VMABl",
                        },
                        {
                            mediaType: MediaType.IMAGE,
                            src: getStockPhotoUrl(
                                "communities",
                                "rear-view-group-school-friends-walking-outdoors-lifestyle.webp"
                            ),
                            alt: "Group of school friends walking outdoors",
                            videoSrc: defaultVideoUrl,
                            videoAutoplay: false,
                            videoControls: true,
                            videoLoop: false,
                            iframeSrc:
                                "https://www.youtube.com/embed/LxIR7eFw8HI?si=ObbVhSBAi35VMABl",
                        },
                    ],
                    {
                        mediaType: MediaType.IMAGE,
                        src: getStockPhotoUrl(
                            "communities",
                            "portrait-smart-adult-woman-working-laptop.webp"
                        ),
                        alt: "Smart adult woman working with laptop",
                        videoSrc: defaultVideoUrl,
                        videoAutoplay: false,
                        videoControls: true,
                        videoLoop: false,
                        iframeSrc: "https://www.youtube.com/embed/LxIR7eFw8HI?si=ObbVhSBAi35VMABl",
                    }
                )
            )
            .withStyleFields([
                colorDefaults.value.background,
                colorDefaults.value.text,
                colorDefaults.value.brand,
            ])
            .build()
    );

    const simpleTwoColumnsBlock = computed<BlockDef>(() =>
        createBlock<SimpleTwoColumnsProps>({
            typeId: "SimpleTwoColumns",
            type: "gallery",
            name: t("page.blocks.gallery.simpleTwoColumns"),
            component: SimpleTwoColumns,
        })
            .withField(
                "tag",
                headingField(translations.value.tag, "Together", "h3", {
                    text: translations.value.text,
                    tag: translations.value.tag,
                })
            )
            .withField(
                "title",
                headingField(translations.value.title, "Building Connections That Matter", "h2", {
                    text: translations.value.text,
                    tag: translations.value.tag,
                })
            )
            .withField(
                "description",
                htmlField(
                    translations.value.description,
                    "From urban adventures to park gatherings, discover the moments that strengthen friendships and create lasting bonds in our vibrant community.",
                    translations.value.description
                )
            )
            .withArrayField(
                "images",
                arrayField(
                    translations.value.image,
                    "images",
                    {
                        mediaType: { label: translations.value.mediaType, type: "media" },
                        src: { label: translations.value.src, type: "media" },
                        alt: { label: translations.value.alt, type: "media" },
                        videoSrc: { label: translations.value.videoSrc, type: "media" },
                        videoAutoplay: { label: translations.value.videoAutoplay, type: "media" },
                        videoControls: { label: translations.value.videoControls, type: "media" },
                        videoLoop: { label: translations.value.videoLoop, type: "media" },
                        iframeSrc: { label: translations.value.iframeSrc, type: "media" },
                    },
                    [
                        {
                            mediaType: MediaType.IMAGE,
                            src: getStockPhotoUrl(
                                "communities",
                                "multi-ethnic-group-friends-having-fun-together-urban-backg.webp"
                            ),
                            alt: "Multi ethnic group of friends having fun together",
                            videoSrc: defaultVideoUrl,
                            videoAutoplay: false,
                            videoControls: true,
                            videoLoop: false,
                            iframeSrc:
                                "https://www.youtube.com/embed/LxIR7eFw8HI?si=ObbVhSBAi35VMABl",
                        },
                        {
                            mediaType: MediaType.IMAGE,
                            src: getStockPhotoUrl(
                                "communities",
                                "side-view-smiley-friends-outdoors-city.webp"
                            ),
                            alt: "Side view of smiley friends outdoors in city",
                            videoSrc: defaultVideoUrl,
                            videoAutoplay: false,
                            videoControls: true,
                            videoLoop: false,
                            iframeSrc:
                                "https://www.youtube.com/embed/LxIR7eFw8HI?si=ObbVhSBAi35VMABl",
                        },
                        {
                            mediaType: MediaType.IMAGE,
                            src: getStockPhotoUrl(
                                "communities",
                                "smiley-friends-outdoors-having-fun-city.webp"
                            ),
                            alt: "Smiley friends outdoors having fun in city",
                            videoSrc: defaultVideoUrl,
                            videoAutoplay: false,
                            videoControls: true,
                            videoLoop: false,
                            iframeSrc:
                                "https://www.youtube.com/embed/LxIR7eFw8HI?si=ObbVhSBAi35VMABl",
                        },
                        {
                            mediaType: MediaType.IMAGE,
                            src: getStockPhotoUrl(
                                "communities",
                                "group-people-hugging-each-other-park.webp"
                            ),
                            alt: "Group of people hugging each other in park",
                            videoSrc: defaultVideoUrl,
                            videoAutoplay: false,
                            videoControls: true,
                            videoLoop: false,
                            iframeSrc:
                                "https://www.youtube.com/embed/LxIR7eFw8HI?si=ObbVhSBAi35VMABl",
                        },
                    ],
                    {
                        mediaType: MediaType.IMAGE,
                        src: getStockPhotoUrl("communities", "friends-relaxing-park-bench.webp"),
                        alt: "Friends relaxing on park bench",
                        videoSrc: defaultVideoUrl,
                        videoAutoplay: false,
                        videoControls: true,
                        videoLoop: false,
                        iframeSrc: "https://www.youtube.com/embed/LxIR7eFw8HI?si=ObbVhSBAi35VMABl",
                    }
                )
            )
            .withStyleFields([
                colorDefaults.value.background,
                colorDefaults.value.text,
                colorDefaults.value.brand,
            ])
            .build()
    );

    const withPanelBlock = computed<BlockDef>(() =>
        createBlock<WithPanelProps>({
            typeId: "WithPanel",
            type: "gallery",
            name: t("page.blocks.gallery.withPanel"),
            component: WithPanel,
        })
            .withField(
                "tag",
                headingField(translations.value.tag, "Digital Life", "h3", {
                    text: translations.value.text,
                    tag: translations.value.tag,
                })
            )
            .withField(
                "title",
                headingField(translations.value.title, "Connected Through Technology", "h2", {
                    text: translations.value.text,
                    tag: translations.value.tag,
                })
            )
            .withField(
                "description",
                htmlField(
                    translations.value.description,
                    "Experience the seamless blend of technology and communication - where creativity meets connectivity in our digital-first world.",
                    translations.value.description
                )
            )
            .withArrayField(
                "images",
                arrayField(
                    translations.value.image,
                    "images",
                    {
                        mediaType: { label: translations.value.mediaType, type: "media" },
                        src: { label: translations.value.src, type: "media" },
                        alt: { label: translations.value.alt, type: "media" },
                        videoSrc: { label: translations.value.videoSrc, type: "media" },
                        videoAutoplay: { label: translations.value.videoAutoplay, type: "media" },
                        videoControls: { label: translations.value.videoControls, type: "media" },
                        videoLoop: { label: translations.value.videoLoop, type: "media" },
                        iframeSrc: { label: translations.value.iframeSrc, type: "media" },
                    },
                    [
                        {
                            mediaType: MediaType.IMAGE,
                            src: getStockPhotoUrl(
                                "communities",
                                "close-up-mobile-phone-used-by-teenage-entertainer-doing-video.webp"
                            ),
                            alt: "Close up mobile phone used by teenage entertainer doing video",
                            videoSrc: defaultVideoUrl,
                            videoAutoplay: false,
                            videoControls: true,
                            videoLoop: false,
                            iframeSrc:
                                "https://www.youtube.com/embed/LxIR7eFw8HI?si=ObbVhSBAi35VMABl",
                        },
                        {
                            mediaType: MediaType.IMAGE,
                            src: getStockPhotoUrl(
                                "communities",
                                "close-up-woman-using-keyboard.webp"
                            ),
                            alt: "Close up woman using keyboard",
                            videoSrc: defaultVideoUrl,
                            videoAutoplay: false,
                            videoControls: true,
                            videoLoop: false,
                            iframeSrc:
                                "https://www.youtube.com/embed/LxIR7eFw8HI?si=ObbVhSBAi35VMABl",
                        },
                        {
                            mediaType: MediaType.IMAGE,
                            src: getStockPhotoUrl(
                                "communities",
                                "woman-talking-with-her-friends-online.webp"
                            ),
                            alt: "Woman talking with her friends online",
                            videoSrc: defaultVideoUrl,
                            videoAutoplay: false,
                            videoControls: true,
                            videoLoop: false,
                            iframeSrc:
                                "https://www.youtube.com/embed/LxIR7eFw8HI?si=ObbVhSBAi35VMABl",
                        },
                        {
                            mediaType: MediaType.IMAGE,
                            src: getStockPhotoUrl(
                                "communities",
                                "smiling-redhead-girl-using-laptop-looking.webp"
                            ),
                            alt: "Smiling redhead girl using laptop",
                            videoSrc: defaultVideoUrl,
                            videoAutoplay: false,
                            videoControls: true,
                            videoLoop: false,
                            iframeSrc:
                                "https://www.youtube.com/embed/LxIR7eFw8HI?si=ObbVhSBAi35VMABl",
                        },
                    ],
                    {
                        mediaType: MediaType.IMAGE,
                        src: getStockPhotoUrl(
                            "communities",
                            "woman-sitting-home-using-laptop.webp"
                        ),
                        alt: "Woman sitting at home using laptop",
                        videoSrc: defaultVideoUrl,
                        videoAutoplay: false,
                        videoControls: true,
                        videoLoop: false,
                        iframeSrc: "https://www.youtube.com/embed/LxIR7eFw8HI?si=ObbVhSBAi35VMABl",
                    }
                )
            )
            .withStyleFields([
                colorDefaults.value.background,
                colorDefaults.value.text,
                colorDefaults.value.brand,
            ])
            .build()
    );

    const asymmetricGridBlock = computed<BlockDef>(() =>
        createBlock<AsymmetricGridProps>({
            typeId: "AsymmetricGrid",
            type: "gallery",
            name: t("page.blocks.gallery.asymmetricGrid"),
            component: AsymmetricGrid,
        })
            .withField(
                "tag",
                headingField(translations.value.tag, "Professionals", "h3", {
                    text: translations.value.text,
                    tag: translations.value.tag,
                })
            )
            .withField(
                "title",
                headingField(translations.value.title, "Excellence in Every Field", "h2", {
                    text: translations.value.text,
                    tag: translations.value.tag,
                })
            )
            .withField(
                "description",
                htmlField(
                    translations.value.description,
                    "Meet the dedicated professionals who drive innovation forward - from healthcare to business networking, see expertise in action.",
                    translations.value.description
                )
            )
            .withArrayField(
                "images",
                arrayField(
                    translations.value.image,
                    "images",
                    {
                        mediaType: { label: translations.value.mediaType, type: "media" },
                        src: { label: translations.value.src, type: "media" },
                        alt: { label: translations.value.alt, type: "media" },
                        videoSrc: { label: translations.value.videoSrc, type: "media" },
                        videoAutoplay: { label: translations.value.videoAutoplay, type: "media" },
                        videoControls: { label: translations.value.videoControls, type: "media" },
                        videoLoop: { label: translations.value.videoLoop, type: "media" },
                        iframeSrc: { label: translations.value.iframeSrc, type: "media" },
                    },
                    [
                        {
                            mediaType: MediaType.IMAGE,
                            src: getStockPhotoUrl(
                                "communities",
                                "networking-concept-still-life-arrangement.webp"
                            ),
                            alt: "Networking concept still life arrangement",
                            videoSrc: defaultVideoUrl,
                            videoAutoplay: false,
                            videoControls: true,
                            videoLoop: false,
                            iframeSrc:
                                "https://www.youtube.com/embed/LxIR7eFw8HI?si=ObbVhSBAi35VMABl",
                        },
                        {
                            mediaType: MediaType.IMAGE,
                            src: getStockPhotoUrl(
                                "communities",
                                "grey-laptop-with-white-stationery.webp"
                            ),
                            alt: "Grey laptop with white stationery",
                            videoSrc: defaultVideoUrl,
                            videoAutoplay: false,
                            videoControls: true,
                            videoLoop: false,
                            iframeSrc:
                                "https://www.youtube.com/embed/LxIR7eFw8HI?si=ObbVhSBAi35VMABl",
                        },
                        {
                            mediaType: MediaType.IMAGE,
                            src: getStockPhotoUrl("communities", "doctor-working-with-laptop.webp"),
                            alt: "Doctor working with laptop",
                            videoSrc: defaultVideoUrl,
                            videoAutoplay: false,
                            videoControls: true,
                            videoLoop: false,
                            iframeSrc:
                                "https://www.youtube.com/embed/LxIR7eFw8HI?si=ObbVhSBAi35VMABl",
                        },
                        {
                            mediaType: MediaType.IMAGE,
                            src: getStockPhotoUrl(
                                "communities",
                                "rear-view-group-friends-showing-standing-together.webp"
                            ),
                            alt: "Rear view group of friends showing standing together",
                            videoSrc: defaultVideoUrl,
                            videoAutoplay: false,
                            videoControls: true,
                            videoLoop: false,
                            iframeSrc:
                                "https://www.youtube.com/embed/LxIR7eFw8HI?si=ObbVhSBAi35VMABl",
                        },
                    ],
                    {
                        mediaType: MediaType.IMAGE,
                        src: getStockPhotoUrl(
                            "communities",
                            "man-using-laptop-night-top-view-web-banner-with-copy-space.webp"
                        ),
                        alt: "Man using laptop at night top view",
                        videoSrc: defaultVideoUrl,
                        videoAutoplay: false,
                        videoControls: true,
                        videoLoop: false,
                        iframeSrc: "https://www.youtube.com/embed/LxIR7eFw8HI?si=ObbVhSBAi35VMABl",
                    }
                )
            )
            .withStyleFields([
                colorDefaults.value.background,
                colorDefaults.value.text,
                colorDefaults.value.brand,
            ])
            .build()
    );

    const asymmetricMosaicGridBlock = computed<BlockDef>(() =>
        createBlock<AsymmetricMosaicGridProps>({
            typeId: "AsymmetricMosaicGrid",
            type: "gallery",
            name: t("page.blocks.gallery.asymmetricMosaicGrid"),
            component: AsymmetricMosaicGrid,
        })
            .withField(
                "tag",
                headingField(translations.value.tag, "Global Team", "h3", {
                    text: translations.value.text,
                    tag: translations.value.tag,
                })
            )
            .withField(
                "title",
                headingField(translations.value.title, "Work Without Boundaries", "h2", {
                    text: translations.value.text,
                    tag: translations.value.tag,
                })
            )
            .withField(
                "description",
                htmlField(
                    translations.value.description,
                    "From airplane wifi to late-night collaborations, witness the future of work where location is no limit to productivity and human connection.",
                    translations.value.description
                )
            )
            .withArrayField(
                "images",
                arrayField(
                    translations.value.image,
                    "images",
                    {
                        mediaType: { label: translations.value.mediaType, type: "media" },
                        src: { label: translations.value.src, type: "media" },
                        alt: { label: translations.value.alt, type: "media" },
                        videoSrc: { label: translations.value.videoSrc, type: "media" },
                        videoAutoplay: { label: translations.value.videoAutoplay, type: "media" },
                        videoControls: { label: translations.value.videoControls, type: "media" },
                        videoLoop: { label: translations.value.videoLoop, type: "media" },
                        iframeSrc: { label: translations.value.iframeSrc, type: "media" },
                    },
                    [
                        {
                            mediaType: MediaType.IMAGE,
                            src: getStockPhotoUrl(
                                "communities",
                                "blonde-female-tourist-checking-incoming-notification-smartphone-sitting-seat-airplane-with-netbookyoung-businesswoman-share-media-from-telephone-laptop-computer-plane.webp"
                            ),
                            alt: "Blonde female tourist checking smartphone notification on airplane",
                            videoSrc: defaultVideoUrl,
                            videoAutoplay: false,
                            videoControls: true,
                            videoLoop: false,
                            iframeSrc:
                                "https://www.youtube.com/embed/LxIR7eFw8HI?si=ObbVhSBAi35VMABl",
                        },
                        {
                            mediaType: MediaType.IMAGE,
                            src: getStockPhotoUrl(
                                "communities",
                                "medium-shot-man-working-night-with-device.webp"
                            ),
                            alt: "Medium shot man working at night with device",
                            videoSrc: defaultVideoUrl,
                            videoAutoplay: false,
                            videoControls: true,
                            videoLoop: false,
                            iframeSrc:
                                "https://www.youtube.com/embed/LxIR7eFw8HI?si=ObbVhSBAi35VMABl",
                        },
                        {
                            mediaType: MediaType.IMAGE,
                            src: getStockPhotoUrl(
                                "communities",
                                "silhouette-business-people-discussion-meeting-cityscape-team-concept.webp"
                            ),
                            alt: "Silhouette business people discussion meeting cityscape",
                            videoSrc: defaultVideoUrl,
                            videoAutoplay: false,
                            videoControls: true,
                            videoLoop: false,
                            iframeSrc:
                                "https://www.youtube.com/embed/LxIR7eFw8HI?si=ObbVhSBAi35VMABl",
                        },
                        {
                            mediaType: MediaType.IMAGE,
                            src: getStockPhotoUrl(
                                "communities",
                                "blonde-female-tourist-checking-incoming-notification-smartphone-sitting-seat-airplane-with-netbookyoung-businesswoman-share-media-from-telephone-laptop-computer-plane-2.webp"
                            ),
                            alt: "Blonde female tourist with smartphone and laptop on airplane",
                            videoSrc: defaultVideoUrl,
                            videoAutoplay: false,
                            videoControls: true,
                            videoLoop: false,
                            iframeSrc:
                                "https://www.youtube.com/embed/LxIR7eFw8HI?si=ObbVhSBAi35VMABl",
                        },
                        {
                            mediaType: MediaType.IMAGE,
                            src: getStockPhotoUrl(
                                "communities",
                                "diverse-friends-gathering-together.webp"
                            ),
                            alt: "Diverse friends gathering together",
                            videoSrc: defaultVideoUrl,
                            videoAutoplay: false,
                            videoControls: true,
                            videoLoop: false,
                            iframeSrc:
                                "https://www.youtube.com/embed/LxIR7eFw8HI?si=ObbVhSBAi35VMABl",
                        },
                        {
                            mediaType: MediaType.IMAGE,
                            src: getStockPhotoUrl("communities", "friends-stacking-hands.webp"),
                            alt: "Friends stacking hands",
                            videoSrc: defaultVideoUrl,
                            videoAutoplay: false,
                            videoControls: true,
                            videoLoop: false,
                            iframeSrc:
                                "https://www.youtube.com/embed/LxIR7eFw8HI?si=ObbVhSBAi35VMABl",
                        },
                    ],
                    {
                        mediaType: MediaType.IMAGE,
                        src: getStockPhotoUrl("communities", "people-using-smartphones.webp"),
                        alt: "People using smartphones",
                        videoSrc: defaultVideoUrl,
                        videoAutoplay: false,
                        videoControls: true,
                        videoLoop: false,
                        iframeSrc: "https://www.youtube.com/embed/LxIR7eFw8HI?si=ObbVhSBAi35VMABl",
                    }
                )
            )
            .withStyleFields([
                colorDefaults.value.background,
                colorDefaults.value.text,
                colorDefaults.value.brand,
            ])
            .build()
    );

    const blocks = computed(() => [
        simpleThreeColumnsBlock.value,
        simpleTwoColumnsBlock.value,
        withPanelBlock.value,
        asymmetricGridBlock.value,
        asymmetricMosaicGridBlock.value,
    ]);

    return {
        blocks,
    };
}
